import React, { useCallback, useEffect, useRef, useState } from "react";
import logger from "@/lib/logger";
import { Link } from "react-router-dom";
import { SignOut, ChatCircleDots, MagnifyingGlass, Bell, Broadcast, FilmStrip } from "@phosphor-icons/react";
import AppShell from "@/components/AppShell";
import PostCard from "@/components/PostCard";
import StoriesRing from "@/components/StoriesRing";
import FeedComposerPrompt from "@/components/FeedComposerPrompt";
import ReelsRail from "@/components/ReelsRail";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const PAGE_SIZE = 10;

export default function FeedPage() {
  const { user, logout } = useAuth();
  const [posts, setPosts] = useState(null);
  const [stories, setStories] = useState([]);
  const [unread, setUnread] = useState(0);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  // Guard against duplicate paginated requests when the observer fires multiple
  // times in quick succession (e.g. user scrolls fast through the sentinel).
  const inflightRef = useRef(false);
  // StrictMode in dev double-invokes effects; this ref prevents the first-page
  // fetch from firing twice on mount (production renders this once anyway).
  const firstLoadRef = useRef(false);
  // "↑ N new posts" pill — peeks at the top of the feed every 60s. If we see
  // post_ids we don't already have in our local list, surface a tap-to-refresh
  // pill so the user gets fresh content without manually pulling-to-refresh.
  const [newCount, setNewCount] = useState(0);
  const knownIdsRef = useRef(new Set());

  const loadFirstPage = useCallback(async () => {
    if (firstLoadRef.current) return;
    firstLoadRef.current = true;
    try {
      const [feedRes, { data: st }] = await Promise.all([
        api.get(`/feed?limit=${PAGE_SIZE}`),
        api.get("/stories"),
      ]);
      const list = feedRes.data || [];
      setPosts(list);
      knownIdsRef.current = new Set(list.map((p) => p.post_id));
      setNewCount(0);
      setNextCursor(feedRes.headers?.["x-next-cursor"] || null);
      setStories(st);
    } catch (e) {
      toast.error("Failed to load feed");
      setPosts([]);
      setNextCursor(null);
    } finally {
      firstLoadRef.current = false;
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!nextCursor || inflightRef.current) return;
    inflightRef.current = true;
    setLoadingMore(true);
    try {
      const res = await api.get(`/feed?limit=${PAGE_SIZE}&cursor=${encodeURIComponent(nextCursor)}`);
      const more = res.data || [];
      setPosts((prev) => {
        // Dedupe in case the same post id slipped through (e.g. a new post was
        // inserted between fetches and shifted the cursor row).
        const seen = new Set((prev || []).map((p) => p.post_id));
        return [...(prev || []), ...more.filter((p) => !seen.has(p.post_id))];
      });
      // Track paginated post_ids too so the "↑ N new posts" pill doesn't
      // falsely flag a post the user already loaded by scrolling down.
      for (const p of more) knownIdsRef.current.add(p.post_id);
      setNextCursor(res.headers?.["x-next-cursor"] || null);
    } catch (e) {
      logger.warn("feed", "load more failed", e);
    } finally {
      inflightRef.current = false;
      setLoadingMore(false);
    }
  }, [nextCursor]);

  const loadUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.unread || 0);
    } catch (e) {
      logger.warn("feed", "unread fetch failed", e);
    }
  }, []);

  useEffect(() => { loadFirstPage(); loadUnread(); }, [loadFirstPage, loadUnread]);

  // --- Scroll restoration ----------------------------------------------------
  // When the user taps into a post and comes back, restore the feed to where
  // they were. We persist scrollY to sessionStorage (debounced) and restore
  // once after the first page renders. Cleared on hard nav away from the feed
  // (the cleanup function below).
  const SCROLL_KEY = "voxly:feed:scrollY";

  // Restore once — only after we have posts to scroll past.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current || !posts || posts.length === 0) return;
    const raw = sessionStorage.getItem(SCROLL_KEY);
    if (!raw) return;
    const y = parseInt(raw, 10);
    if (!Number.isFinite(y) || y <= 0) return;
    restoredRef.current = true;
    // Double rAF so React has actually committed the post cards before we
    // try to scroll past them, otherwise window.scrollTo clamps to 0.
    requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo({ top: y, behavior: "instant" })));
  }, [posts]);

  // Persist current scrollY (throttled to every 150 ms) so back-nav has
  // something to restore.
  useEffect(() => {
    let t = null;
    const onScroll = () => {
      if (t) return;
      t = setTimeout(() => {
        t = null;
        sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
      }, 150);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); if (t) clearTimeout(t); };
  }, []);

  // Poll for new posts at the top of the feed every 60 seconds. Lightweight
  // single-request check — we only diff post_ids and show the pill if there
  // are unseen ones. The actual refresh runs when the user taps the pill.
  useEffect(() => {
    const tick = async () => {
      // Don't fight an in-progress page load.
      if (firstLoadRef.current || inflightRef.current) return;
      try {
        const { data } = await api.get(`/feed?limit=${PAGE_SIZE}`);
        const fresh = (data || []).filter((p) => !knownIdsRef.current.has(p.post_id));
        if (fresh.length > 0) setNewCount(fresh.length);
      } catch { /* network blip — try again next tick */ }
    };
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, []);

  // IntersectionObserver — fires `loadMore` when the sentinel scrolls into view.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !nextCursor) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) loadMore();
    }, { rootMargin: "400px 0px" });
    io.observe(node);
    return () => io.disconnect();
  }, [nextCursor, loadMore]);

  useEffect(() => {
    const onRefresh = () => loadFirstPage();
    const onRead = () => setUnread(0);
    const onHidden = (e) => setPosts((arr) => arr ? arr.filter((p) => p.post_id !== e.detail) : arr);
    const onEdited = () => loadFirstPage();
    window.addEventListener("voxly:refresh-stories", onRefresh);
    window.addEventListener("voxly:notifications-read", onRead);
    window.addEventListener("voxly:post-hidden", onHidden);
    window.addEventListener("voxly:post-edited", onEdited);
    const id = setInterval(loadUnread, 30000);
    return () => {
      window.removeEventListener("voxly:refresh-stories", onRefresh);
      window.removeEventListener("voxly:notifications-read", onRead);
      window.removeEventListener("voxly:post-hidden", onHidden);
      window.removeEventListener("voxly:post-edited", onEdited);
      clearInterval(id);
    };
  }, [loadFirstPage, loadUnread]);

  return (
    <AppShell>
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-background/85 border-b border-border/60 px-4 py-3 flex items-center justify-between" data-testid="feed-header">
        <Logo size={34} />
        <div className="flex items-center gap-1">
          <Link to="/rooms" data-testid="feed-rooms-link" aria-label="Live Rooms" className="relative p-2 text-foreground hover:text-primary transition">
            <Broadcast size={22} />
          </Link>
          <Link to="/reels" data-testid="feed-reels-link" aria-label="Reels" className="relative p-2 text-foreground hover:text-primary transition">
            <FilmStrip size={22} weight="fill" />
          </Link>
          <Link to="/search" data-testid="feed-search-link" aria-label="Search" className="p-2 text-foreground hover:text-primary transition">
            <MagnifyingGlass size={22} />
          </Link>
          <Link to="/notifications" data-testid="feed-notifications-link" aria-label="Notifications" className="relative p-2 text-foreground hover:text-primary transition">
            <Bell size={22} />
            {unread > 0 && (
              <span data-testid="notifications-badge" className="absolute top-1 right-1 h-4 min-w-[16px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center border-2 border-background">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </Link>
          <Link to="/dms" data-testid="feed-dms-link" aria-label="Voice DMs" className="h-10 w-10 rounded-full flex items-center justify-center text-foreground hover:text-primary hover:bg-secondary/60 active:scale-90 transition">
            <ChatCircleDots size={24} weight="bold" />
          </Link>
          <button onClick={logout} data-testid="logout-button" aria-label="Logout" className="p-2 text-muted-foreground hover:text-foreground transition">
            <SignOut size={20} />
          </button>
        </div>
      </header>

      {newCount > 0 && (
        <div className="sticky top-[60px] z-10 flex justify-center px-4 pointer-events-none" data-testid="feed-new-posts-pill-wrap">
          <button
            type="button"
            onClick={async () => {
              firstLoadRef.current = false; // ensure fresh load isn't blocked
              await loadFirstPage();
              // Smoothly scroll back to the top of the list so the user sees
              // the new content right where they expect it.
              if (typeof window !== "undefined") {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
            data-testid="feed-new-posts-pill"
            className="pointer-events-auto mt-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-xs font-bold shadow-lg ring-1 ring-primary/40 active:scale-95 hover:brightness-110 transition flex items-center gap-1.5"
          >
            <span aria-hidden="true">↑</span>
            {newCount} new {newCount === 1 ? "post" : "posts"}
          </button>
        </div>
      )}

      <FeedComposerPrompt user={user} />
      <StoriesRing stories={stories} />

      <div data-testid="feed-list">
        {posts === null && (
          <div className="px-4 py-12 flex justify-center">
            <div className="h-8 w-8 rounded-full border-2 border-primary/60 border-t-transparent animate-spin" />
          </div>
        )}
        {posts && posts.length === 0 && (
          <div className="px-6 py-16 text-center">
            <h2 className="font-heading text-2xl font-bold">Your feed is quiet.</h2>
            <p className="mt-2 text-sm text-muted-foreground">Tap the <span className="text-primary font-semibold">+ button</span> below to share your first moment, or visit <Link to="/discover" className="text-primary font-semibold">Discover</Link> to find people.</p>
          </div>
        )}
        {posts && posts.map((p, i) => (
          <React.Fragment key={p.post_id}>
            <PostCard
              post={p}
              onCommentAdded={loadFirstPage}
              onDeleted={(id) => setPosts((arr) => arr.filter((x) => x.post_id !== id))}
            />
            {/* Inject a Reels rail after every 4th post — Facebook-style discovery loop */}
            {i > 0 && (i + 1) % 4 === 0 && i < posts.length - 1 && (
              <ReelsRail key={`rail-${i}`} />
            )}
          </React.Fragment>
        ))}

        {/* Smart pagination sentinel + "load more" affordances */}
        {posts && posts.length > 0 && nextCursor && (
          <div ref={sentinelRef} data-testid="feed-load-more-sentinel" className="px-4 py-8 flex flex-col items-center gap-3">
            {loadingMore ? (
              <div className="h-7 w-7 rounded-full border-2 border-primary/60 border-t-transparent animate-spin" data-testid="feed-load-more-spinner" />
            ) : (
              <button
                type="button"
                onClick={loadMore}
                data-testid="feed-load-more-btn"
                className="px-5 py-2 rounded-full bg-secondary/60 border border-border text-xs font-bold uppercase tracking-widest text-foreground/80 hover:text-foreground hover:bg-secondary active:scale-95 transition"
              >
                Load more
              </button>
            )}
          </div>
        )}
        {posts && posts.length > 0 && !nextCursor && (
          <div className="px-6 py-10 text-center text-xs font-mono uppercase tracking-widest text-muted-foreground" data-testid="feed-end-marker">
            You're all caught up.
          </div>
        )}
      </div>
    </AppShell>
  );
}
